import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Button, Box } from '@mui/material';
import { fetchStationTextData } from '../services/stationService';

interface ForecastCardProps {
  date: string;
  text: string;
}

const ForecastCard: React.FC<ForecastCardProps> = ({ date, text }) => {
  return (
    <Card elevation={12} sx={{ 
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1) !important', 
      border: '1px solid rgba(0, 0, 0, 0.12)',
      bgcolor: getBackgroundColor(date),
      '&.MuiCard-root': {
        backgroundColor: getBackgroundColor(date),
      }
    }}>
      <CardContent sx={{ 
        padding: { xs: '8px', sm: '16px' },
        '&:last-child': {
          paddingBottom: { xs: '8px', sm: '16px' }  // Override default last-child padding
        }
      }}>
        <Typography 
          component="div"
          variant="body1" 
          sx={{ 
            whiteSpace: 'pre-wrap', 
            textAlign: 'left',
            fontSize: { xs: '0.75rem', sm: '1rem' },
            mb: { xs: 0, sm: '0.35em' }  // Remove bottom margin on mobile
          }}
        >
          <span>{date.toUpperCase()}</span>
          <span>... </span>
          {parseTextSegments(text).map((segment, index) => (
            <span key={index} style={{ fontWeight: segment.isBold ? 700 : 400 }}>
              {segment.text}
            </span>
          ))}
        </Typography>
      </CardContent>
    </Card>
  );
};

interface SkiAreaTextDataProps {
  stationValue: string;
}

interface TextSegment {
  text: string;
  isBold: boolean;
}

const parseTextSegments = (text: string): TextSegment[] => {
  const segments: TextSegment[] = [];
  let currentIndex = 0;

  while (currentIndex < text.length) {
    const boldStart = text.indexOf('<b>', currentIndex);
    if (boldStart === -1) {
      // Add remaining text as non-bold segment
      if (currentIndex < text.length) {
        segments.push({
          text: text.slice(currentIndex),
          isBold: false
        });
      }
      break;
    }

    // Add text before bold tag if exists
    if (boldStart > currentIndex) {
      segments.push({
        text: text.slice(currentIndex, boldStart),
        isBold: false
      });
    }

    const boldEnd = text.indexOf('</b>', boldStart);
    if (boldEnd === -1) {
      // Unclosed bold tag, treat rest as non-bold
      segments.push({
        text: text.slice(currentIndex),
        isBold: false
      });
      break;
    }

    // Add bold segment
    segments.push({
      text: text.slice(boldStart + 3, boldEnd),
      isBold: true
    });

    currentIndex = boldEnd + 4;
  }

  return segments;
};

interface SkiAreaData {
  ski_area?: {
    metadata?: {
      name: string;
      top_elevation: number;
      base_elevation: number;
    };
  };
  daily_text?: Record<string, {
    text: string;
    period: string;
  }>;
  generated_at?: string;
}

const getBackgroundColor = (date: string) => {
  const color = date.includes('Day') || date.includes('Today')
    ? '#fffaf0'  // Very light warm color for day/today
    : date.includes('Night') || date.includes('Tonight')
    ? '#f8faff'  // Very light cool color for night/tonight
    : '#ffffff';  // Pure white for both
  return color;
};

export const SkiAreaTextData: React.FC<SkiAreaTextDataProps> = ({ stationValue }) => {
  const [textData, setTextData] = useState<SkiAreaData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showExtended, setShowExtended] = useState(false);

  useEffect(() => {
    const loadTextData = async () => {
      try {
        const data = await fetchStationTextData(stationValue);
        setTextData(data);
        setError(null);
      } catch (err) {
        setError('Failed to load text data');
        console.error(err);
      }
    };

    // Reset states when station changes
    setShowHistory(false);
    setShowExtended(false);

    if (stationValue) {
      loadTextData();
    }
  }, [stationValue]);

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  if (!textData) {
    return <div>Loading text data...</div>;
  }

  // Extract metadata and daily text from the textData
  const metadata = textData.ski_area?.metadata;
  const dailyText = textData.daily_text;

  return (
    <Box key={stationValue} sx={{ 
      margin: { xs: '0px 6px', sm: '0px 20px' },
      padding: { xs: '4px 8px', sm: '8px 20px' },
      borderRadius: '4px'
    }}>
      {metadata && (
        <Box className="metadata-section" sx={{ mb: '16px' }}>
          <h2>{metadata.name}</h2>
          <div>Summit: {Math.round(metadata.top_elevation)}'  / Base: {Math.round(metadata.base_elevation)}'</div>
          {textData.generated_at && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: '0px' }}>
              Updated {Math.round((Date.now() - Date.parse(textData.generated_at)) / 60000)} minutes ago
            </Typography>
          )}
        </Box>
      )}
      
      {dailyText && Object.keys(dailyText).length > 0 ? (
        <>
          {/* History Section */}
          <Box sx={{ mb: '8px' }}>
            {!showHistory ? (
              <Button 
                variant="outlined" 
                onClick={() => setShowHistory(true)}
                sx={{ mb: '4px' }}
              >
                Show History
              </Button>
            ) : (
              <Box sx={{ display: 'grid', gap: { xs: '8px', sm: '16px' } }}>
                {Object.entries(dailyText)
                  .filter(([_, forecast]) => forecast.period === 'history')
                  .map(([date, forecast]) => (
                    <ForecastCard key={date} date={date} text={forecast.text} />
                  ))}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'grid', gap: { xs: '8px', sm: '16px' }, mb: '16px' }}>
            {Object.entries(dailyText)
              .filter(([_, forecast]) => forecast.period === 'forecast')
              .map(([date, forecast]) => (
                <ForecastCard key={date} date={date} text={forecast.text} />
              ))}
          </Box>

          {/* Extended Forecast Section */}
          <Box>
            {!showExtended ? (
              <Button 
                variant="outlined" 
                onClick={() => setShowExtended(true)}
                sx={{ mb: '4px' }}
              >
                Show Extended Forecast
              </Button>
            ) : (
              <Box sx={{ display: 'grid', gap: { xs: '8px', sm: '16px' } }}>
                {Object.entries(dailyText)
                  .filter(([_, forecast]) => forecast.period === 'extended')
                  .map(([date, forecast]) => (
                    <ForecastCard key={date} date={date} text={forecast.text} />
                  ))}
              </Box>
            )}
          </Box>
        </>
      ) : (
        <div>No daily forecast data available</div>
      )}
    </Box>
  );
};
